import {
  IUserSubscription,
  useSubscription,
} from "@/contexts/SubscriptionContext";
import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  getAvatarUrl,
  updateUserProfile,
  uploadAvatarToStorage,
} from "@/lib/userApi";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

interface IProps {
  onClose: () => void;
}

type FormFields = {
  name: string;
  photo: string;
  photoFile: File | null;
};

export default function ProfileEditModal({ onClose }: IProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { subscription } = useSubscription();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting },
  } = useForm<FormFields>({
    defaultValues: {
      name: subscription.username,
      photo: subscription.avatar ? getAvatarUrl(subscription.avatar) : null,
      photoFile: null,
    },
  });

  const [errorMsg, setErrorMsg] = useState("");
  const photoUrl = watch("photo");

  function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      setValue("photoFile", file);
      setValue("photo", URL.createObjectURL(file));
    }
  }

  async function onSubmit(values: FormFields) {
    setErrorMsg("");
    let avatarFileName = subscription.avatar || null;
    try {
      if (values.photoFile) {
        avatarFileName = await uploadAvatarToStorage(values.photoFile);
      }
      await updateUserProfile(
        subscription.user_id,
        values.name,
        avatarFileName
      );
      onClose();
      navigate(0);
    } catch (err: any) {
      setErrorMsg("Ошибка: " + err.message);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-gray-600 rounded-lg shadow-xl w-full max-w-md text-foreground">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t("community.profileEditModal.title")}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6">
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-white">
                {t("community.profileEditModal.profilePhoto")}
              </label>
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gray-800 rounded-full overflow-hidden">
                  {photoUrl && (
                    <img
                      src={photoUrl}
                      alt="Profile preview"
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div>
                  <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7,10 12,5 17,10" />
                      <line x1="12" y1="5" x2="12" y2="15" />
                    </svg>
                    <span className="ml-2"><span className="ml-2">{t("community.profileEditModal.changePhoto")}</span></span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handlePhotoChange}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-white">
                {t("community.profileEditModal.fullName")}
              </label>
              <input
                type="text"
                {...register("name")}
                className="w-full px-3 py-2 border bg-background border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t("community.profileEditModal.fullNamePlaceholder")}
              />
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 p-6 border-t bg-background">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {t("community.profileEditModal.cancel")}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {t("community.profileEditModal.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
